import { useEffect, useRef, useState } from "react";

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Animate a number from 0 → `target` when it first becomes available. Used for
 * the dashboard hero figures. Returns `target` immediately (no animation) when
 * reduced-motion is set, or the value is not finite — so
 * callers can use it unconditionally.
 */
export function useCountUp(target: number, durationMs = 1000): number {
  // Start at 0 when we're going to animate (so the real figure never flashes on
  // first paint before counting up), otherwise start at the real value. The flag
  // is set at bootstrap before React renders, so reading it here is reliable.
  const [value, setValue] = useState<number>(() =>
    !prefersReducedMotion() && Number.isFinite(target) && target !== 0 ? 0 : target
  );
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion() || !Number.isFinite(target)) {
      setValue(target);
      return;
    }

    setValue(0);
    let start: number | null = null;
    const tick = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / durationMs, 1);
      setValue(target * easeOutCubic(p));
      if (p < 1) {
        frame.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [target, durationMs]);

  return value;
}
